/**
 * Mobile Stats — Full player browser
 * Reuses Free Agent Search hooks/components but defaults to ALL players
 */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { dynastyTheme } from '../../../services/colorService';

import { leaguesAPI } from '../../../services/apiService';
import { formatApiError } from '../../../services/errorUtils';
import { useCommissioner } from '../../../contexts/CommissionerContext';
import { useAssignmentLeagueConfig } from '../../../hooks/useAssignmentLeagueConfig';
import MobileLeagueNavigation from '../components/MobileLeagueNavigation';
// 9o.6.b: migrated to mobile-internal hooks + provider per audit Section A.6.
// MobileStats has its own per-page provider instance (independent selection state
// from MobileFreeAgentSearch); no shared root provider at MobileApp level.
import {
  MobileBatchSelectionProvider,
  useMobileBatchSelection,
} from './free-agent-search/MobileBatchSelectionProvider';
import { canAddPlayers } from '../../../utils/RosterCapacityUtils';

import {
  useMobileSearchFilters,
  useMobileLeagueContext,
  useMobilePlayerData,
  useMobilePlayerAssignment,
} from './free-agent-search/hooks';
import MobileSearchControls from './free-agent-search/MobileSearchControls';
import MobileStatusBanners from './free-agent-search/MobileStatusBanners';
import MobilePlayerTable from './free-agent-search/MobilePlayerTable';
import { MobilePlayerModal, MobilePlayerCard } from '../components/player';
import MobilePositionAssignmentModal from '../components/MobilePositionAssignmentModal';
import MobileRosterPlanModal from '../components/MobileRosterPlanModal';
import MobileDropDesignationModal from '../components/MobileDropDesignationModal';
import { useRosterPlan } from '../../../hooks/rosterPlan';
import MobilePageHeader from '../components/MobilePageHeader';
import useDefaultLeague from '../../../hooks/useDefaultLeague';
import { getWaiverActionState } from '../../../utils/waiverStatus';
import { useWaiverClaimFlow } from '../../../hooks/useWaiverClaimFlow';
// DD-MODAL-0723: tell the modal's ownership-bearing session cache the world changed.
import { invalidatePlayer } from '../components/player/hooks/playerDataCache';

// ========================================
// WRAPPER
// ========================================
const MobileStats = () => {
  const [leagues, setLeagues] = useState([]);
  const [league, setLeague] = useState(null);

  const { defaultLeagueId: leagueId } = useDefaultLeague(leagues, true);

  useEffect(() => {
    leaguesAPI.getMyLeagues()
      .then(res => { if (res?.success && res.leagues) setLeagues(res.leagues); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (leagueId) {
      leaguesAPI.getLeagueDetails(leagueId)
        .then(res => { if (res?.success && res.league) setLeague(res.league); })
        .catch(() => {});
    }
  }, [leagueId]);

  if (!leagueId) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', backgroundColor: 'transparent' }}>
        <p style={{ color: dynastyTheme.tokens.colors.neutral[400] }}>Loading leagues...</p>
      </div>
    );
  }

  return (
    <MobileBatchSelectionProvider>
      <MobileStatsInner leagueId={leagueId} league={league} />
    </MobileBatchSelectionProvider>
  );
};

// ========================================
// INNER COMPONENT
// ========================================
const MobileStatsInner = ({ leagueId, league }) => {
  const navigate = useNavigate();
  const {
    isCommissionerMode,
    activeTeamId,
    activeTeamName,
    getTargetTeamId,
    isCommissioner
  } = useCommissioner();

  const { getSelectedPlayerObjects, clearAllSelections } = useMobileBatchSelection();

  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [isPlayerCardOpen, setIsPlayerCardOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  // Canonical league config for assignment surfaces (DD-FIXES-0705 Fix 2):
  // ONE source, §5 nested shape, fail-loud — replaces the hand-merged flat
  // fullLeagueDetails spread and its catch-fallback to the page league.
  const {
    league: assignmentLeague,
    error: leagueConfigError,
  } = useAssignmentLeagueConfig(leagueId);

  // ========================================
  // HOOKS — same as Free Agent Search
  // ========================================
  const searchFilters = useMobileSearchFilters();
  const leagueContext = useMobileLeagueContext(leagueId, isCommissionerMode);
  const playerData = useMobilePlayerData(leagueId, searchFilters, leagueContext);

  // Default to showing ALL players (not just free agents)
  useEffect(() => {
    searchFilters.setShowAll(true);
  }, []);


  const positionAssignment = useMobilePlayerAssignment(leagueId, assignmentLeague, {
    transactionsEnabled: leagueContext.transactionsEnabled,
    leagueStatus: leagueContext.leagueStatus,
    isCommissionerMode,
    activeTeamName,
    getTargetTeamId
  });

  // Bug 1 follow-up (Bible § 4.5.20, 2026-05-15): drop-shuffle plan for
  // mobile-stats ADD button. Mirrors MobileFreeAgentSearch wiring.
  const rosterPlan = useRosterPlan({
    leagueId,
    candidate: positionAssignment.playerForAssignment,
    currentRoster: positionAssignment.currentRoster,
    isCommissionerMode,
    getTargetTeamId,
  });

  // DD-WAIVER-0723-BUILD W2: waiver-claim flow for the stats-page ADD/CLAIM
  // button. On-waivers players route here BEFORE position assignment.
  // Commissioner mode acts as the active team; otherwise the hook resolves
  // the viewer's own team id for cooling-off checks.
  const waiverClaim = useWaiverClaimFlow({
    leagueId,
    myTeamId: isCommissionerMode ? activeTeamId : null,
    fetchMyTeamId: !isCommissionerMode,
    capacityAnalysis: positionAssignment.rosterCapacityAnalysis,
    currentRoster: positionAssignment.currentRoster,
    onSuccess: (claimedPlayer, message) => {
      // DD-MODAL-0723: the claim changed this player's pending state.
      invalidatePlayer(leagueId, claimedPlayer?.ids?.mlb);
      playerData.setSuccessMessage(message);
      setTimeout(() => playerData.setSuccessMessage(''), 5000);
    },
    onError: (message) => {
      playerData.setError(message);
      setTimeout(() => playerData.setError(''), 6000);
    },
  });

  // ========================================
  // HANDLERS — copied from MobileFreeAgentSearch
  // ========================================
  const handleAddPlayer = useCallback(async (player) => {
    const isOfflineDraft = leagueContext.leagueStatus === 'offline_draft';
    const isDraftMode = ['draft', 'offline_draft'].includes(leagueContext.leagueStatus);

    if (!isOfflineDraft && !isDraftMode && !isCommissionerMode && !leagueContext.transactionsEnabled) {
      playerData.setError('Transactions are not allowed until the commissioner sets player prices');
      setTimeout(() => playerData.setError(''), 5000);
      return;
    }

    const isOwned = player.league_context?.team?.team_name;
    if (isOwned) {
      playerData.setError('Player is already owned by a team');
      setTimeout(() => playerData.setError(''), 5000);
      return;
    }

    // W2 waiver gate (Bible § 4.5.19): on-waivers players go through the
    // waiver-claim flow, never position assignment / direct add (which the
    // backend rejects). Fires BEFORE the assignment flow.
    const waiverState = getWaiverActionState(player, waiverClaim.myTeamId);
    if (waiverState.onWaivers && !waiverState.expired) {
      if (waiverState.coolingOff) {
        playerData.setError('Cooling Off — you cut this player; wait for waivers to clear');
        setTimeout(() => playerData.setError(''), 5000);
        return;
      }
      // Load roster capacity for the drop-designation picker. loadRosterCapacity
      // returns the fresh analysis (state isn't readable in the same tick).
      const analysis = positionAssignment.rosterCapacityAnalysis
        || await positionAssignment.loadRosterCapacity();
      waiverClaim.openClaim(player, { capacityAnalysis: analysis || null });
      return;
    }

    if (!positionAssignment.rosterCapacityAnalysis) {
      await positionAssignment.loadRosterCapacity();
    }

    positionAssignment.setPlayerForAssignment(player);
    positionAssignment.setShowPositionModal(true);
  }, [leagueContext.transactionsEnabled, leagueContext.leagueStatus, isCommissionerMode, positionAssignment, playerData, waiverClaim]);

  const executePlayerAssignment = useCallback(async (player, assignment) => {
    const firstName = player.info?.first_name;
    const lastName = player.info?.last_name;
    const leaguePlayerId = player.ids?.league_player;

    positionAssignment.setAddingPlayer(leaguePlayerId);
    playerData.setError('');

    try {
      // Bible Rule 9: missing required data is a bug — throw, not fallback.
      if (!leaguePlayerId) {
        throw new Error(
          `Cannot add player: missing ids.league_player (mlb=${player.ids?.mlb}, ` +
          `name=${player.info?.full_name}). Player object likely came from non-canonical source.`
        );
      }
      if (!assignment?.type) {
        throw new Error('Cannot add player: assignment.type is required');
      }
      if (assignment.type === 'active' && !assignment.slotId) {
        throw new Error(
          `Cannot add ${player.info?.full_name} to active roster: missing assignment.slotId`
        );
      }

      // 4-branch contract resolution per Bible § 4.5 contract model:
      //   - Minors: zero out
      //   - Offseason w/ projected_contract (line 1027): keeper rights
      //   - Owned in-season: existing roster_data.financial
      //   - FA pickup (default): baseline_price + league policy (§ 4.5.1 line 809, § 4.5.2 line 821)
      const isOffseason = ['setup', 'draft', 'finalizing'].includes(leagueContext.leagueStatus);
      const isOwnedAlready = player.league_context?.status === 'owned';
      const isMinors = assignment.type === 'minors';
      const playerName = player.info?.full_name;
      const effectiveLeague = assignmentLeague;

      let playerSalary;
      let contractYears;
      if (isMinors) {
        playerSalary = 0;
        contractYears = 0;
      } else if (isOffseason && player.projected_contract) {
        playerSalary = player.projected_contract.baseline_price;
        contractYears = player.projected_contract.contract_years;
        if (playerSalary == null) {
          throw new Error(`Cannot add ${playerName}: missing projected_contract.baseline_price`);
        }
        if (contractYears == null) {
          throw new Error(`Cannot add ${playerName}: missing projected_contract.contract_years`);
        }
      } else if (isOwnedAlready) {
        playerSalary = player.league_context.financial?.contract_salary;
        contractYears = player.league_context.financial?.contract_years;
        if (playerSalary == null) {
          throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_salary on owned player`);
        }
        if (contractYears == null) {
          throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_years on owned player`);
        }
      } else {
        // Bible § 4.5.1 line 809 + § 10 line 1701: FA pickup price = baseline_price
        // (NOT contract_salary — fix mobile divergence to match desktop + canonical).
        // Bible § 4.5.2 line 821: contract_years = league.salary_settings.standard_contract_length.
        playerSalary = player.league_context?.financial?.baseline_price;
        contractYears = effectiveLeague?.salary_settings?.standard_contract_length;
        if (playerSalary == null) {
          throw new Error(
            `Cannot add ${playerName}: missing baseline_price on league_context.financial. ` +
            `Pricing engine may not have run for this league.`
          );
        }
        if (contractYears == null) {
          throw new Error(
            `Cannot add ${playerName}: league.salary_settings.standard_contract_length is not configured.`
          );
        }
      }

      const payload = {
        league_player_id: leaguePlayerId,
        salary: playerSalary,
        contract_years: contractYears,
        roster_status: assignment.type,
        roster_position: assignment.type === 'active' ? assignment.slotId : null,
        start_contract: !isMinors
      };

      if (isCommissionerMode) {
        payload.commissioner_action = true;
        payload.target_team_id = getTargetTeamId(null);
      }

      const response = await leaguesAPI.addPlayerToTeam(leagueId, payload);
      invalidatePlayer(leagueId, player?.ids?.mlb);

      if (response.success) {
        const teamName = isCommissionerMode ? activeTeamName : 'your team';
        const posText = assignment.type === 'active' ? assignment.position.toUpperCase() : assignment.type;
        playerData.setSuccessMessage(`${response.player_name || firstName + ' ' + lastName} added to ${teamName} (${posText})!`);
        playerData.loadPlayers(0, false);
        await positionAssignment.loadRosterCapacity();
        window.dispatchEvent(new Event('roster-updated'));
        setTimeout(() => playerData.setSuccessMessage(''), 3000);
      } else {
        playerData.setError(response.message || response.error?.message || response.error || 'Failed to add player');
      }
    } catch (err) {
      // 9o.6.d-followup: was missing both JSON.stringify safety net AND err.response.data.detail
      // check — produced generic "Request failed with status code 404" for HTTPException routes.
      // formatApiError now handles all 3 backend envelopes.
      playerData.setError(formatApiError(err, 'Failed to add player'));
    } finally {
      positionAssignment.setAddingPlayer(null);
      positionAssignment.setShowPositionModal(false);
      positionAssignment.setPlayerForAssignment(null);
    }
  }, [isCommissionerMode, activeTeamName, getTargetTeamId, leagueId, playerData, positionAssignment]);

  const handlePlayerClick = useCallback((player) => {
    const playerId = player.ids?.mlb;
    setSelectedPlayerId(playerId?.toString());
    setIsPlayerModalOpen(true);
  }, []);

  const handleExpandToCard = () => { setIsPlayerModalOpen(false); setIsPlayerCardOpen(true); };
  const handleCollapseToModal = () => { setIsPlayerCardOpen(false); setIsPlayerModalOpen(true); };
  const handleClosePlayer = () => { setIsPlayerModalOpen(false); setIsPlayerCardOpen(false); setSelectedPlayerId(null); };

  const handleLeagueNavigate = useCallback((pageId) => {
    if (leagueId) {
      navigate(`/leagues/${leagueId}`, { state: { initialPage: pageId } });
    }
  }, [leagueId, navigate]);

  // ========================================
  // LOADING
  // ========================================
  if (!leagueContext.isReady) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', backgroundColor: 'transparent' }}>
        <div style={{
          width: '30px', height: '30px',
          border: `2px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
          borderTop: `2px solid ${dynastyTheme.tokens.colors.primary}`,
          borderRadius: '50%', animation: 'spin 1s linear infinite',
          margin: '0 auto 10px',
        }} />
        <p style={{ color: dynastyTheme.tokens.colors.neutral[400], fontSize: '13px' }}>Loading...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ========================================
  // STATE OBJECT FOR CHILD COMPONENTS
  // ========================================
  const freeAgentState = {
    players: playerData.players,
    totalCount: playerData.totalCount,
    loading: playerData.loading,
    backgroundLoading: playerData.backgroundLoading,
    hasMore: playerData.hasMore,
    page: playerData.page,
    error: playerData.error,
    setError: playerData.setError,
    successMessage: playerData.successMessage,
    setSuccessMessage: playerData.setSuccessMessage,
    loadPlayers: playerData.loadPlayers,
    searchTerm: searchFilters.searchTerm,
    setSearchTerm: searchFilters.setSearchTerm,
    activeTab: searchFilters.activeTab,
    setActiveTab: searchFilters.setActiveTab,
    positionFilter: searchFilters.positionFilter,
    setPositionFilter: searchFilters.setPositionFilter,
    viewMode: searchFilters.viewMode,
    setViewMode: searchFilters.setViewMode,
    showAll: searchFilters.showAll,
    setShowAll: searchFilters.setShowAll,
    includeNon40Man: searchFilters.includeNon40Man,
    setIncludeNon40Man: searchFilters.setIncludeNon40Man,
    leagueStatus: leagueContext.leagueStatus,
    transactionsEnabled: leagueContext.transactionsEnabled,
    browseMode: leagueContext.browseMode,
    noPricesWarning: leagueContext.noPricesWarning,
    pricesExist: leagueContext.pricesExist,
    handleBrowseAnyway: leagueContext.handleBrowseAnyway,
    addingPlayer: positionAssignment.addingPlayer,
    rosterCapacityAnalysis: positionAssignment.rosterCapacityAnalysis,
    handlePlayerClick,
    handleAddPlayer
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div style={{ backgroundColor: 'transparent' }}>
      {/* League nav bar */}
      {leagueId && (
        <MobileLeagueNavigation currentPage="stats" onNavigate={handleLeagueNavigate} />
      )}

      {/* Shared header */}
      <MobilePageHeader
        title={league?.league_name || 'Player Browser'}
        pageBadge="Stats"
        bannerUrl={league?.league_settings?.league_banner_url}
        showLogo={false}
      />

      <MobileStatusBanners
        state={freeAgentState}
        isCommissionerMode={isCommissionerMode}
        activeTeamName={activeTeamName}
      />

      {(leagueContext.transactionsEnabled || leagueContext.browseMode || true) && (
        <div style={{ padding: '0 8px' }}>
          <MobileSearchControls
            state={freeAgentState}
            leagueId={leagueId}
            isCommissionerMode={isCommissionerMode}
            activeTeamName={activeTeamName}
          />

          <MobilePlayerTable
            state={freeAgentState}
            leagueId={leagueId}
            isCommissionerMode={isCommissionerMode}
            activeTeamName={activeTeamName}
            activeTeamId={waiverClaim.myTeamId}
            handlePlayerClick={handlePlayerClick}
          />
        </div>
      )}

      {/* Position Assignment Modal */}
      <MobilePositionAssignmentModal
        player={positionAssignment.playerForAssignment}
        league={assignmentLeague}
        currentRoster={positionAssignment.currentRoster}
        onAssign={(assignmentData) => {
          const player = assignmentData.player || positionAssignment.playerForAssignment;
          if (!player) return;

          // Bible Rule 9: missing required data is a bug — surface it loudly.
          const rosterStatus = assignmentData.roster_status;
          if (!rosterStatus) {
            playerData.setError('Cannot add player: modal returned no roster_status');
            return;
          }
          // Position can come either as canonical roster_position or as `assignment`
          // slot id — both are valid keys for the same data, not a silent default.
          const rosterPosition = assignmentData.roster_position ?? assignmentData.assignment;

          let position = 'bench';
          let slotId = 'bench';
          if (rosterStatus === 'minors') { position = 'minors'; slotId = 'minors'; }
          else if (rosterStatus === 'bench') { position = 'bench'; slotId = 'bench'; }
          else if (rosterStatus === 'active' && rosterPosition) {
            position = rosterPosition.split('_')[0];
            slotId = rosterPosition;
          } else if (rosterStatus === 'active') {
            playerData.setError('Cannot add to active roster: modal returned no slot id');
            return;
          }
          executePlayerAssignment(player, { position, slotId, type: rosterStatus, reason: 'Manual selection', priority: 1 });
        }}
        onCancel={() => {
          positionAssignment.setShowPositionModal(false);
          positionAssignment.setPlayerForAssignment(null);
        }}
        isVisible={positionAssignment.showPositionModal}
        mode="add"
        onOpenDropShuffle={() => {
          positionAssignment.setShowPositionModal(false);
          rosterPlan.open();
        }}
      />

      {/* MobileRosterPlanModal — drop-shuffle plan for stats-page ADD.
          Per Bible § 4.5.10 atomic drop+add via /plan-add. */}
      <MobileRosterPlanModal
        candidate={positionAssignment.playerForAssignment}
        currentRoster={positionAssignment.currentRoster}
        state={rosterPlan.state}
        plans={rosterPlan.plans}
        selectedDropPlayer={rosterPlan.selectedDropPlayer}
        selectedPlan={rosterPlan.selectedPlan}
        errorClass={rosterPlan.errorClass}
        errorMessage={rosterPlan.errorMessage}
        infeasibleReason={rosterPlan.infeasibleReason}
        onPickDrop={rosterPlan.requestPlans}
        onChangeDrop={rosterPlan.changeDrop}
        onSelectPlan={rosterPlan.selectPlan}
        onConfirm={rosterPlan.executePlan}
        onCancel={() => rosterPlan.cancel()}
        isVisible={rosterPlan.state !== 'idle' && rosterPlan.state !== 'cancelled'}
      />

      {/* W2: waiver-claim modal (context='waiver_claim') — same submit flow
          as MobileFreeAgentSearch (leaguesAPI.submitWaiverClaim via
          useWaiverClaimFlow). */}
      <MobileDropDesignationModal {...waiverClaim.modalProps} />

      {/* Player Modal */}
      <MobilePlayerModal
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isPlayerModalOpen}
        onClose={handleClosePlayer}
        onExpandToCard={handleExpandToCard}
      />

      {/* Player Card */}
      <MobilePlayerCard
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isPlayerCardOpen}
        onClose={handleClosePlayer}
        onCollapse={handleCollapseToModal}
      />
    </div>
  );
};

export default MobileStats;
